// ===========================================
// Enquiry Funnel (#371 Phase 7, enquiries plan §12)
// ===========================================
// How far the enquiries that came in during the window got, from the stage history: each bar
// counts the enquiries that reached that stage OR A LATER ONE ("got at least this far", Laura
// 26/10/2026), so the bars only shrink. Then the months, by source and by closing reason as tables
// (the table IS the accessible view). Spam and "To review" are left out and say so; migrated
// enquiries count from where the migration put them. No CSV (plan §12).
//
// One series, so one hue (the brand primary), no legend (the title names it), every bar directly
// labelled with its count and share; the text wears text colours, never the bar's.

import {
  Alert,
  Box,
  Card,
  CardContent,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import type { EnquiryFunnel } from '../../api/reports.api';
import { brandColors } from '../../styles/brandColors';

interface EnquiryFunnelChartProps {
  data: EnquiryFunnel | undefined;
  loading?: boolean;
  isError?: boolean;
}

/** `YYYY-MM-DD` → dd/mm/yyyy (a school-local day key from the server; never parsed as a Date). */
const dmy = (key: string): string => key.split('-').reverse().join('/');
/** One decimal, as the server's `conversionRate`, so the headline and the Enrolled bar agree (review N2). */
const pct = (part: number, whole: number): string => (whole === 0 ? '0%' : `${Math.round((part / whole) * 1000) / 10}%`);

export default function EnquiryFunnelChart({ data, loading, isError }: EnquiryFunnelChartProps) {
  if (loading) {
    return (
      <Card>
        <CardContent>
          <Skeleton variant="text" width={240} />
          <Skeleton variant="rectangular" height={220} sx={{ mt: 2 }} />
        </CardContent>
      </Card>
    );
  }
  if (isError || !data) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Enquiry funnel</Typography>
          <Alert severity="error">Could not load the enquiry funnel. Please try again.</Alert>
        </CardContent>
      </Card>
    );
  }

  const { totals, stages } = data;
  const showWaitlist = data.waitlistEnabled || totals.waitlist > 0;
  const enrolled = totals.reached.ENROLLED ?? 0;

  return (
    <Card>
      <CardContent>
        <Typography variant="h6">Enquiry funnel</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          How far the enquiries that came in between {dmy(data.window.from)} and {dmy(data.window.to)} got. Each bar counts the
          enquiries that reached that stage or a later one.
        </Typography>

        {totals.total === 0 ? (
          <Alert severity="info">No enquiries came in during this period.</Alert>
        ) : (
          <>
            {/* The headline: what share enrolled. */}
            <Stack direction="row" spacing={4} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
              <Stat label="Enquiries" value={String(totals.total)} />
              <Stat label="Enrolled" value={`${totals.conversionRate}%`} caption={`${enrolled} of ${totals.total}`} />
              {showWaitlist && <Stat label={data.sideLabels.waitlist} value={String(totals.waitlist)} caption="ever waitlisted" />}
              <Stat label={data.sideLabels.notProceeding} value={String(totals.notProceeding)} caption="now" />
            </Stack>

            {/* The funnel: one bar per stage, anchored left, labelled at its end. */}
            <Stack spacing={1} sx={{ mb: 3 }} role="list" aria-label="Enquiries that reached each stage">
              {stages.map(({ stage, label }) => {
                const count = totals.reached[stage] ?? 0;
                return (
                  <Tooltip
                    key={stage}
                    title={`${label}: ${count} of ${totals.total} enquiries (${pct(count, totals.total)}) got at least this far`}
                    placement="top"
                  >
                    <Box role="listitem" data-testid={`funnel-row-${stage}`} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ width: { xs: 110, sm: 160 }, flexShrink: 0, overflowWrap: 'anywhere' }}>
                        {label}
                      </Typography>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box
                          data-testid={`funnel-bar-${stage}`}
                          sx={{
                            height: 18,
                            width: `${totals.total === 0 ? 0 : (count / totals.total) * 100}%`,
                            minWidth: count > 0 ? 4 : 0,
                            bgcolor: brandColors.primary,
                            borderRadius: '0 4px 4px 0',
                          }}
                        />
                      </Box>
                      <Typography variant="body2" sx={{ width: 84, flexShrink: 0, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {count} <Box component="span" sx={{ color: 'text.secondary' }}>({pct(count, totals.total)})</Box>
                      </Typography>
                    </Box>
                  </Tooltip>
                );
              })}
            </Stack>

            {/* By month: the table view of the same counts. */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>By month received</Typography>
            <TableContainer sx={{ mb: 3, overflowX: 'auto' }}>
              <Table size="small" aria-label="Enquiries by month received">
                <TableHead sx={{ '& th': { whiteSpace: 'nowrap' } }}>
                  <TableRow>
                    <TableCell>Month</TableCell>
                    <TableCell align="right">Enquiries</TableCell>
                    {stages.slice(1).map(({ stage, label }) => (
                      <TableCell key={stage} align="right">{label}</TableCell>
                    ))}
                    {showWaitlist && <TableCell align="right">{data.sideLabels.waitlist}</TableCell>}
                    <TableCell align="right">{data.sideLabels.notProceeding}</TableCell>
                    <TableCell align="right">Enrolled %</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...data.months].reverse().map((m) => (
                    <TableRow key={m.month}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{m.monthLabel}</TableCell>
                      <TableCell align="right">{m.total}</TableCell>
                      {stages.slice(1).map(({ stage }) => (
                        <TableCell key={stage} align="right">{m.reached[stage] ?? 0}</TableCell>
                      ))}
                      {showWaitlist && <TableCell align="right">{m.waitlist}</TableCell>}
                      <TableCell align="right">{m.notProceeding}</TableCell>
                      <TableCell align="right">{m.total === 0 ? '—' : `${m.conversionRate}%`}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>By source</Typography>
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table size="small" aria-label="Enquiries by source">
                    <TableHead>
                      <TableRow>
                        <TableCell>Source</TableCell>
                        <TableCell align="right">Enquiries</TableCell>
                        <TableCell align="right">Enrolled</TableCell>
                        <TableCell align="right">Enrolled %</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.bySource.map((s) => (
                        <TableRow key={s.id ?? 'none'}>
                          <TableCell>{s.label}</TableCell>
                          <TableCell align="right">{s.total}</TableCell>
                          <TableCell align="right">{s.enrolled}</TableCell>
                          <TableCell align="right">{s.conversionRate}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Why enquiries did not go ahead</Typography>
                {data.byClosedReason.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">None in this period.</Typography>
                ) : (
                  <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small" aria-label="Enquiries by closing reason">
                      <TableHead>
                        <TableRow>
                          <TableCell>Reason</TableCell>
                          <TableCell align="right">Enquiries</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.byClosedReason.map((r) => (
                          <TableRow key={r.id ?? 'none'}>
                            <TableCell>{r.label}</TableCell>
                            <TableCell align="right">{r.count}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </Stack>
          </>
        )}

        {(data.migrated > 0 || data.excluded.spam > 0 || data.excluded.toReview > 0) && (
          <Stack spacing={0.5} sx={{ mt: 2 }} data-testid="funnel-notes">
            {data.migrated > 0 && (
              <Typography variant="caption" color="text.secondary">
                {data.migrated} {data.migrated === 1 ? 'enquiry' : 'enquiries'} came over from the old enquiry statuses and{' '}
                {data.migrated === 1 ? 'counts' : 'count'} from the stage the move gave {data.migrated === 1 ? 'it' : 'them'}: how far{' '}
                {data.migrated === 1 ? 'it' : 'they'} got before that is not known.
              </Typography>
            )}
            {(data.excluded.spam > 0 || data.excluded.toReview > 0) && (
              <Typography variant="caption" color="text.secondary">
                Not counted: {notCounted(data.excluded)}.
              </Typography>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

/** "1 spam, and 4 waiting for an outcome…", from the non-zero parts only (review N3). */
function notCounted({ spam, toReview }: EnquiryFunnel['excluded']): string {
  const parts = [
    spam > 0 ? `${spam} spam` : null,
    toReview > 0 ? `${toReview} waiting for an outcome on the To review screen` : null,
  ].filter((p): p is string => p !== null);
  return parts.join(', and ');
}

function Stat({ label, value, caption }: { label: string; value: string; caption?: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Typography variant="h6" sx={{ lineHeight: 1.2 }}>{value}</Typography>
      {caption && <Typography variant="caption" color="text.secondary">{caption}</Typography>}
    </Box>
  );
}
